import { useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import type { WorkspaceApp } from '../../../lib/app-shares';
import { recordAppOpen } from '../../../lib/app-shares';
import { relativeOpenedAt } from '../../../lib/app-recency';
import { Badge, Card, OverflowMenu, Typography } from '../ui';
import { ownsApp } from '../../../lib/app-ownership';
import { ShareAppDialog } from './ShareAppDialog';
import { DeleteAppDialog } from './DeleteAppDialog';
import styles from './WorkspaceAppsSection.module.css';

/**
 * "Dana Levi" -> "Dana". The card has one line for this and a full name crowds out the Space beside it;
 * the owner is somebody the reader knows by first name, since they chose to share with them.
 */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

export function AppsGrid({
  apps,
  spaceNames,
  onOpenBi,
  onDeleted,
  onOpened,
}: {
  apps: WorkspaceApp[];
  /** Space id -> name, for the card's meta line. The app records only the id. */
  spaceNames?: Record<string, string>;
  onOpenBi?: (appId: string) => void;
  /** Told which app went, so the caller can drop it from its own list without a refetch. */
  onDeleted?: (appId: string) => void;
  /** Told which app was opened, once the stamp landed, so the ordering can catch up. */
  onOpened?: (appId: string) => void;
}) {
  const { t } = useTranslation();
  const [sharing, setSharing] = useState<WorkspaceApp | null>(null);
  // ONE `now` for the whole grid, captured per render. Reading the clock per card would let two cards
  // in the same list disagree about where the boundary between "today" and "this week" falls.
  const now = new Date();
  /**
   * The "when" a card shows, or undefined when there is none to show.
   *
   * The gate and the value must be the SAME expression. Gating on `app.lastOpenedAt` (a string) while
   * printing `relativeOpenedAt(...)` (the parsed result) disagree for a non-empty stamp that
   * `Date.parse` rejects: the truthy string takes the date branch and the parse returns undefined, so
   * the card rendered an EMPTY span where either a date or the "Never opened" badge belongs — the exact
   * "empty slot beside a filled one reads as a fault" this design set out to remove. Both native
   * surfaces gate on the parsed label; this now does too.
   */
  const openedLabel = (app: WorkspaceApp) => relativeOpenedAt(app.lastOpenedAt, now);
  const spaceOf = (app: WorkspaceApp) => (app.workspaceId ? spaceNames?.[app.workspaceId] : undefined);
  const [deleting, setDeleting] = useState<WorkspaceApp | null>(null);

  const open = (app: WorkspaceApp) => {
    if (!app.url) return;
    onOpenBi?.(app.id);
    // Stamp the open so the list can order by it. NOT awaited, and its failure is swallowed by the
    // client: the tab below must open at the same instant whatever the server says about bookkeeping.
    // `onOpened` lets the screen move the card without waiting for a refetch.
    void recordAppOpen(app.id).then(() => onOpened?.(app.id));
    // A new tab, and `noopener` with it: the app is third-party code, and without this it gets a handle
    // on the Symphony window through `window.opener`.
    window.open(app.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div className={styles.grid}>
        {apps.map((app) => (
          <Card
            key={app.id}
            size="sm"
            className={styles.card}
            // Keyboard reaches it too — a div you can only click is a control half the users cannot use.
            role={app.url ? 'button' : undefined}
            tabIndex={app.url ? 0 : undefined}
            style={app.url ? { cursor: 'pointer' } : undefined}
            onClick={app.url ? () => open(app) : undefined}
            onKeyDown={
              app.url
                ? (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      open(app);
                    }
                  }
                : undefined
            }
          >
            <Typography variant="p1" color="strong" component="h3" noWrap>
              {app.name}
            </Typography>
            {app.description && (
              <Typography variant="p3" color="secondary" component="p" className={styles.description}>
                {app.description}
              </Typography>
            )}
            <div className={styles.footer}>
              {/* WHEN, bare. The card is scanned in a grid, so "2 hours ago" carries the whole answer
                  and the word "Opened" in front of it is nine characters of nothing. A never-opened
                  app says so as a PILL instead — a date it does not have cannot be shown, and "—"
                  would be worse than a plain statement. */}
              {openedLabel(app) ? (
                <Typography variant="p3" color="muted" component="span">
                  {openedLabel(app)}
                </Typography>
              ) : (
                <Badge variant="active" label={t('apps.card.neverOpened')} />
              )}
              {/* Whose app, and where it lives — the two facts that tell one card from another once
                  the screenshots are gone. A shared app names its owner possessively and drops the
                  Space, because a shared app sits in no Space of the reader's: showing both would
                  put an empty slot next to a filled one. */}
              {!ownsApp(app.workspaceId) ? (
                <Badge
                  variant="waiting-on-a-reply"
                  label={
                    app.ownerName?.trim()
                      ? t('apps.card.ownersApp', { name: firstName(app.ownerName) })
                      : t('apps.card.sharedWithYou')
                  }
                />
              ) : spaceOf(app) ? (
                <>
                  <span className={styles.dot} aria-hidden="true" />
                  <Typography variant="p3" color="muted" component="span">
                    {spaceOf(app)}
                  </Typography>
                </>
              ) : null}
              {ownsApp(app.workspaceId) && (
                <span
                  className={styles.menu}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
                >
                  <OverflowMenu
                    ariaLabel={t('apps.rowMenu.label', { name: app.name })}
                    actions={[
                      { key: 'share', label: t('apps.share.action'), onSelect: () => setSharing(app) },
                      { key: 'delete', label: t('apps.delete.action'), destructive: true, onSelect: () => setDeleting(app) },
                    ]}
                  />
                </span>
              )}
            </div>
          </Card>
        ))}
      </div>

      {sharing && <ShareAppDialog app={sharing} onClose={() => setSharing(null)} />}
      {deleting && (
        <DeleteAppDialog
          app={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            onDeleted?.(deleting.id);
            setDeleting(null);
          }}
        />
      )}
    </>
  );
}
